/**
 * Duplicate Account Prevention
 * Utilities for preventing and managing duplicate accounts
 */

import { StoredAccount, AccountStorage } from './accountStorage';
import { AuthSecret } from '../../../types/utils/authSecretTypes';
import { multiAccountErrors } from '../../../constants/multiAccountConstants';

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  existingAccount?: StoredAccount;
  conflictType: 'email' | 'loginName' | 'both' | 'none';
  canMerge: boolean;
  mergeStrategy?: 'update' | 'replace' | 'keep_both';
}

export interface AccountConflictResolution {
  action: 'merge' | 'replace' | 'cancel' | 'keep_both' | 'update';
  keepExisting: boolean;
  updateMetadata: boolean;
}

/**
 * Duplicate account prevention service
 */
export class DuplicateAccountPrevention {
  
  /**
   * Comprehensive duplicate check
   */
  static async checkForDuplicates(
    authSecret: AuthSecret,
    email: string,
    storage: AccountStorage
  ): Promise<DuplicateCheckResult> {
    try {
      const existingAccounts = await storage.getAllAccounts();
      
      // Check for email conflicts
      const emailConflict = existingAccounts.find(
        account => account.email.toLowerCase() === email.toLowerCase()
      );

      // Check for loginName conflicts
      const loginNameConflict = existingAccounts.find(
        account => account.loginName.toLowerCase() === authSecret.loginName.toLowerCase()
      );

      // Determine conflict type
      let conflictType: DuplicateCheckResult['conflictType'] = 'none';
      let existingAccount: StoredAccount | undefined;

      if (emailConflict && loginNameConflict && emailConflict.id === loginNameConflict.id) {
        conflictType = 'both';
        existingAccount = emailConflict;
      } else if (emailConflict) {
        conflictType = 'email';
        existingAccount = emailConflict;
      } else if (loginNameConflict) {
        conflictType = 'loginName';
        existingAccount = loginNameConflict;
      }

      const isDuplicate = conflictType !== 'none';
      const canMerge = isDuplicate && this.canAccountsBeMerged(authSecret, existingAccount!);

      return {
        isDuplicate,
        existingAccount,
        conflictType,
        canMerge,
        mergeStrategy: canMerge ? this.suggestMergeStrategy(authSecret, existingAccount!) : undefined
      };

    } catch (error) {
      console.error('Failed to check for duplicates:', error);
      return {
        isDuplicate: false,
        conflictType: 'none',
        canMerge: false
      };
    }
  }

  /**
   * Check if accounts can be safely merged
   */
  private static canAccountsBeMerged(
    newAuthSecret: AuthSecret,
    existingAccount: StoredAccount
  ): boolean {
    // Accounts can be merged if they represent the same user
    // This is a simplified check - in production, you might want more sophisticated logic
    
    const sameLoginName = newAuthSecret.loginName.toLowerCase() === existingAccount.loginName.toLowerCase();
    const sameUserName = newAuthSecret.userName.toLowerCase() === existingAccount.userName.toLowerCase();
    const sameEmail = newAuthSecret.loginName.toLowerCase() === existingAccount.email.toLowerCase();

    // Can merge if at least 2 out of 3 identifiers match
    const matchCount = [sameLoginName, sameUserName, sameEmail].filter(Boolean).length;
    return matchCount >= 2;
  }

  /**
   * Suggest merge strategy based on account comparison
   */
  private static suggestMergeStrategy(
    newAuthSecret: AuthSecret,
    existingAccount: StoredAccount
  ): DuplicateCheckResult['mergeStrategy'] {
    // If the new account has more recent token, suggest update
    try {
      const newTokenExpiry = new Date(newAuthSecret.token.accessToken_Expires);
      const existingCreatedAt = this.extractCreatedAtFromId(existingAccount.id);
      
      // If new token is more recent, suggest update
      if (newTokenExpiry.getTime() > existingCreatedAt) {
        return 'update';
      }
    } catch (error) {
      console.warn('Failed to compare token dates:', error);
    }

    // Default to update strategy
    return 'update';
  }

  /**
   * Handle duplicate account based on resolution strategy
   */
  static async handleDuplicateAccount(
    authSecret: AuthSecret,
    email: string,
    duplicateResult: DuplicateCheckResult,
    resolution: AccountConflictResolution,
    storage: AccountStorage
  ): Promise<string> {
    try {
      if (!duplicateResult.isDuplicate || !duplicateResult.existingAccount) {
        throw new Error('No duplicate account to handle');
      }

      const existingAccount = duplicateResult.existingAccount;

      switch (resolution.action) {
        case 'cancel':
          throw new Error(multiAccountErrors.DUPLICATE_ACCOUNT);

        case 'replace':
          // Remove existing account and add new one
          await storage.removeAccount(existingAccount.id);
          // The caller will add the new account
          return 'replaced';

        case 'merge':
        case 'update':
          // Update existing account with new credentials
          return await this.mergeAccounts(authSecret, email, existingAccount, storage);

        case 'keep_both':
          // Allow both accounts (modify email to make it unique)
          const uniqueEmail = this.generateUniqueEmail(email, await storage.getAllAccounts());
          return uniqueEmail;

        default:
          throw new Error('Invalid conflict resolution action');
      }

    } catch (error) {
      console.error('Failed to handle duplicate account:', error);
      throw error;
    }
  }

  /**
   * Merge new credentials into existing account
   */
  private static async mergeAccounts(
    newAuthSecret: AuthSecret,
    email: string,
    existingAccount: StoredAccount,
    storage: AccountStorage
  ): Promise<string> {
    try {
      // Create updated account with new credentials but preserve metadata
      const updatedAccount: StoredAccount = {
        ...existingAccount,
        loginName: newAuthSecret.loginName,
        userName: newAuthSecret.userName,
        email: email,
        lastUsed: Date.now()
        // encryptedCredentials will be updated by the caller
      };

      await storage.saveAccount(updatedAccount);
      return existingAccount.id;

    } catch (error) {
      console.error('Failed to merge accounts:', error);
      throw error;
    }
  }

  /**
   * Generate unique email for keep_both strategy
   */
  private static generateUniqueEmail(baseEmail: string, existingAccounts: StoredAccount[]): string {
    const existingEmails = new Set(existingAccounts.map(acc => acc.email.toLowerCase()));
    
    if (!existingEmails.has(baseEmail.toLowerCase())) {
      return baseEmail;
    }

    // Try adding numbers
    for (let i = 2; i <= 99; i++) {
      const [localPart, domain] = baseEmail.split('@');
      const candidateEmail = `${localPart}+${i}@${domain}`;
      
      if (!existingEmails.has(candidateEmail.toLowerCase())) {
        return candidateEmail;
      }
    }

    // Fallback: add timestamp
    const [localPart, domain] = baseEmail.split('@');
    const timestamp = Date.now().toString(36);
    return `${localPart}+${timestamp}@${domain}`;
  }

  /**
   * Extract created timestamp from account ID
   */
  private static extractCreatedAtFromId(accountId: string): number {
    try {
      const parts = accountId.split('_');
      if (parts.length >= 3) {
        return parseInt(parts[2], 36);
      }
    } catch (error) {
      console.warn('Failed to extract timestamp from account ID:', error);
    }
    return Date.now();
  }

  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Normalize email for comparison
   */
  static normalizeEmail(email: string): string {
    return email.toLowerCase().trim();
  }

  /**
   * Check if two accounts represent the same user
   */
  static areAccountsSameUser(account1: StoredAccount, account2: StoredAccount): boolean {
    const normalizedEmail1 = this.normalizeEmail(account1.email);
    const normalizedEmail2 = this.normalizeEmail(account2.email);
    
    const normalizedLogin1 = account1.loginName.toLowerCase();
    const normalizedLogin2 = account2.loginName.toLowerCase();

    // Same if email or loginName match
    return normalizedEmail1 === normalizedEmail2 || normalizedLogin1 === normalizedLogin2;
  }

  /**
   * Find potential duplicate accounts (fuzzy matching)
   */
  static async findPotentialDuplicates(
    storage: AccountStorage
  ): Promise<Array<{ accounts: StoredAccount[]; reason: string }>> {
    try {
      const allAccounts = await storage.getAllAccounts();
      const duplicateGroups: Array<{ accounts: StoredAccount[]; reason: string }> = [];

      // Group by email
      const emailGroups = new Map<string, StoredAccount[]>();
      allAccounts.forEach(account => {
        const normalizedEmail = this.normalizeEmail(account.email);
        if (!emailGroups.has(normalizedEmail)) {
          emailGroups.set(normalizedEmail, []);
        }
        emailGroups.get(normalizedEmail)!.push(account);
      });

      // Find email duplicates
      emailGroups.forEach((accounts, email) => {
        if (accounts.length > 1) {
          duplicateGroups.push({
            accounts,
            reason: `Duplicate email: ${email}`
          });
        }
      });

      // Group by loginName
      const loginGroups = new Map<string, StoredAccount[]>();
      allAccounts.forEach(account => {
        const normalizedLogin = account.loginName.toLowerCase();
        if (!loginGroups.has(normalizedLogin)) {
          loginGroups.set(normalizedLogin, []);
        }
        loginGroups.get(normalizedLogin)!.push(account);
      });

      // Find loginName duplicates (that aren't already email duplicates)
      loginGroups.forEach((accounts, loginName) => {
        if (accounts.length > 1) {
          // Check if this group is already covered by email duplicates
          const alreadyCovered = duplicateGroups.some(group => 
            group.accounts.some(acc => accounts.includes(acc))
          );
          
          if (!alreadyCovered) {
            duplicateGroups.push({
              accounts,
              reason: `Duplicate login name: ${loginName}`
            });
          }
        }
      });

      return duplicateGroups;

    } catch (error) {
      console.error('Failed to find potential duplicates:', error);
      return [];
    }
  }
}